import type { ReactNode } from 'react'

import type { KeyOwner } from '../../api/keys.ts'
import { Panel, PANEL_HAIRLINE } from '../webhooks/Panel.tsx'

import { CopyButton } from './Secret.tsx'

// Who the session resolves to — which is also whose keys the other two cards
// show. Under a ghost session this is the partner being viewed, not the admin
// viewing, which is exactly what the GhostBar above it promises.

const Row = ({
  label,
  children,
  action,
}: {
  label: string
  children: ReactNode
  action?: ReactNode
}) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 14,
      padding: '11px 0',
      borderTop: `1px solid ${PANEL_HAIRLINE}`,
    }}
  >
    <span
      style={{
        flex: 'none',
        width: 110,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {label}
    </span>
    {children}
    {action !== undefined && (
      <>
        <span style={{ flex: '1 1 24px' }} />
        {action}
      </>
    )}
  </div>
)

const Value = ({ children, mono }: { children: string; mono?: boolean }) => (
  <span
    style={{
      fontSize: 13.5,
      color: 'var(--curri-text)',
      overflowWrap: 'anywhere',
      ...(mono ? { fontFamily: 'var(--curri-font-mono)', fontSize: 13 } : {}),
    }}
  >
    {children}
  </span>
)

const Missing = () => (
  <span style={{ fontSize: 13, color: 'var(--curri-text-tertiary)' }}>—</span>
)

export const IdentityCard = ({ owner }: { owner: KeyOwner }) => (
  <Panel style={{ paddingBottom: 8 }}>
    {/* No numeric user id anywhere on this card. It is Curri's internal handle
        — it authenticates nothing and appears in no request a partner makes —
        so the identifier shown is the external ID, which is the one they will
        actually type. */}
    <Panel.Title>Signed in as</Panel.Title>
    <Panel.Description>
      The Curri user your keys authenticate as. The external ID is the username
      half of your Basic credential.
    </Panel.Description>

    <div style={{ marginTop: 14 }}>
      <Row label="Name">
        {owner.name === null ? <Missing /> : <Value>{owner.name}</Value>}
      </Row>
      <Row label="Email">
        {owner.emailAddress === null ? (
          <Missing />
        ) : (
          <Value>{owner.emailAddress}</Value>
        )}
      </Row>
      <Row
        action={
          owner.externalId === null ? undefined : (
            <CopyButton value={owner.externalId} />
          )
        }
        label="External ID"
      >
        {owner.externalId === null ? (
          <Missing />
        ) : (
          <Value mono>{owner.externalId}</Value>
        )}
      </Row>
    </div>
  </Panel>
)
